import { useShareOrderDetailsMutation } from '@src/mutations/ShareOrderDetails.generated'
import { useShareProductDetailsMutation } from '@src/mutations/ShareProductMutation.generated'
import { useShareQuoteDetailsMutation } from '@src/mutations/ShareQuoteDetails.generated'
import { OrderShareableLinkQuery } from '@src/queries/OrderShareableLinkQuery.generated'
import {
  QuoteShareableLinkQuery,
  useQuoteShareableLinkLazyQuery,
} from '@src/queries/QuoteShareableLinkQuery.generated'
import { CatalogType, ShareProductDetailsInput } from '@src/types/graphql-types'
import { deObfuscateCatalogID, ObfuscatedCatalogID } from '@utils/catalogKeyIds'
import { determineCatalogType } from '@utils/searchUtils'
import { ApolloError } from 'apollo-client'
import router from 'next/router'
import {
  ShareDialogItemTypes,
  ShareDialogProductDetailsItemProps,
} from './ShareDialog'

interface ShareDialogItemData {
  shareQuery: Function
  sharePropertyName: string
  shareLoading: boolean
  linkQuery?: Function
  linkPropertyName?: string
  linkData?: OrderShareableLinkQuery | QuoteShareableLinkQuery | undefined
  linkError?: ApolloError | undefined
}

interface ShareDialogTargetItemData {
  order: ShareDialogItemData
  quote: ShareDialogItemData
  product: ShareDialogItemData
}

export const useShareDialog = (
  itemType: ShareDialogItemTypes,
  itemNumber: string,
  product?: ShareDialogProductDetailsItemProps
) => {
  const [shareOrderDetails, { loading: oLoading }] =
    useShareOrderDetailsMutation()

  const [shareQuoteDetails, { loading: qLoading }] =
    useShareQuoteDetailsMutation()

  const [getQuoteShareableLink, { data: qData, error: qError }] =
    useQuoteShareableLinkLazyQuery()

  const [shareProduct] = useShareProductDetailsMutation()

  const getProductInput = (toEmails: string[], fromEmail: string) => {
    const { context } = router.query
    const isMarketplace = product?.isMarketplace
    const catalog = isMarketplace
      ? CatalogType.Marketplace
      : deObfuscateCatalogID(context as ObfuscatedCatalogID)
    const input: ShareProductDetailsInput = {
      productNumber: itemNumber,
      brand: product?.brand.key || '',
      toEmail: toEmails,
      fromEmail: fromEmail,
      catalogType: determineCatalogType(catalog),
    }
    return input
  }

  const shareDialogTargetItemData: ShareDialogTargetItemData = {
    order: {
      shareQuery: async (
        toEmails: string[],
        fromEmail: string, // fromEmail is required otherwise sharelink takes on fromEmail value
        sharedLink: string
      ) =>
        shareOrderDetails({
          variables: { orderNumber: itemNumber, toEmails, sharedLink },
        }),
      sharePropertyName: 'shareOrderDetails',
      shareLoading: oLoading,
    },
    quote: {
      shareQuery: async (
        toEmails: string[],
        fromEmail: string,
        sharedLink: string
      ) =>
        shareQuoteDetails({
          variables: { quoteNumber: itemNumber, toEmails, sharedLink },
        }),
      sharePropertyName: 'shareQuoteDetails',
      shareLoading: qLoading,
      linkQuery: async () =>
        getQuoteShareableLink({
          variables: {
            input: {
              quoteNumber: itemNumber,
            },
          },
        }),
      linkPropertyName: 'quoteShareableLink',
      linkData: qData,
      linkError: qError,
    },
    product: {
      shareQuery: async (toEmails: string[], fromEmail: string) =>
        shareProduct({
          variables: { input: getProductInput(toEmails, fromEmail) },
        }),
      sharePropertyName: 'shareProduct',
      shareLoading: false,
    },
  }

  return shareDialogTargetItemData[itemType]
}
